import { useEffect, useState } from "react";
import { Flame, Zap, Gift, Shield } from "lucide-react";
import type { CheckInResult } from "@/hooks/useStreaks";

interface DailyCheckInModalProps {
  result: CheckInResult;
  onDismiss: () => void;
}

const DailyCheckInModal = ({ result, onDismiss }: DailyCheckInModalProps) => {
  const [visible, setVisible] = useState(false);
  useEffect(() => {
    requestAnimationFrame(() => setVisible(true));
  }, []);

  return (
    <div className="fixed inset-0 z-[150] flex items-center justify-center p-4">
      <div className="absolute inset-0 bg-background/70 backdrop-blur-sm" onClick={onDismiss} />
      <div
        className={`relative w-full max-w-sm rounded-2xl border-2 border-orange-500/40 bg-card p-6 text-center space-y-4 shadow-[0_0_50px_hsl(25_95%_53%/0.2)] transition-all duration-500 ${
          visible ? "scale-100 opacity-100" : "scale-75 opacity-0"
        }`}
      >
        <div className="w-14 h-14 rounded-2xl bg-orange-500/15 flex items-center justify-center mx-auto">
          <Flame className="w-7 h-7 text-orange-500" />
        </div>

        <div>
          <p className="text-xs text-orange-500 uppercase tracking-[0.2em] font-semibold">Daily Check-in</p>
          <h2 className="font-heading text-2xl font-bold text-foreground mt-1">
            {result.login_streak} Day Streak! 🔥
          </h2>
        </div>

        {result.streak_frozen && (
          <div className="flex items-center justify-center gap-2 text-sm text-accent">
            <Shield className="w-4 h-4" />
            <span>Streak freeze saved your streak!</span>
          </div>
        )}

        {result.streak_broken && (
          <p className="text-sm text-muted-foreground">
            Your streak reset, but you're back! Keep going.
          </p>
        )}

        {result.comeback_bonus && (
          <div className="flex items-center justify-center gap-2 text-sm text-primary">
            <Gift className="w-4 h-4" />
            <span>Comeback bonus applied!</span>
          </div>
        )}

        <div className="flex items-center justify-center gap-1.5 text-sm">
          <Zap className="w-4 h-4 text-primary" />
          <span className="font-semibold text-foreground">+{result.xp_bonus} XP</span>
          <span className="text-muted-foreground">earned</span>
        </div>

        <button
          onClick={onDismiss}
          className="w-full py-3 rounded-xl bg-primary text-primary-foreground font-semibold hover:bg-primary/90 transition-colors"
        >
          Let's Go →
        </button>
      </div>
    </div>
  );
};

export default DailyCheckInModal;
